import InfoIcon from "@assets/images/info.svg";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from "@material-ui/core";
import Checkbox from "@saleor/components/Checkbox";
import DecimalFormat from "@saleor/components/DecimalFormat";
import DialogConfirmDelete from "@saleor/components/DialogConfirmDelete";
import MyTypography from "@saleor/components/MyTypography";
import NumberFormat from "@saleor/components/NumberFormat/NumberFormat";
import { SingleSelectField } from "@saleor/components/SingleSelectField";
import Skeleton from "@saleor/components/Skeleton";
import StickyHorizontalScrollBar from "@saleor/components/StickyHorizontalScrollBar";
import TableCellHeader from "@saleor/components/TableCellHeader";
import { VariantStandard } from "@saleor/createOrder/const";
import {
  OrderDetailQueryParams,
  OrderDetailSortField,
  orderDetailUrl,
  orderDetailUrlParam
} from "@saleor/createOrder/urls";
import { getSortQueryOrderDetailVariables } from "@saleor/createOrder/views/sort";
import {
  ContractCheckoutProductVariantQuery,
  ContractCheckoutQuery,
  useCartTotalCheckoutQuery,
  useContractCheckoutProductVariantQuery,
  useContractCheckoutQuery,
  useContractsQuery,
  useDeleteContractCheckoutLinesMutation,
  useUpdateCustomerCartLinesMutation
} from "@saleor/graphql";
import useAppState from "@saleor/hooks/useAppState";
import useNavigator from "@saleor/hooks/useNavigator";
import { SALE_CATEG_1 } from "@saleor/utils/constants";
import createSortHandler from "@saleor/utils/handlers/sortHandler";
// import { mapNodeToChoice } from "@saleor/utils/maps";
import { getArrowDirection, getSortParams } from "@saleor/utils/sort";
import classNames from "classnames";
import isEmpty from "lodash/isEmpty";
import React, { useEffect, useRef, useState } from "react";
import SVG from "react-inlinesvg";
import { FormattedMessage, useIntl } from "react-intl";

import BottomBar from "./BottomBar";
import BoxInfoCustomer from "./BoxInfoCustomer";
import useStyles, { bottomFixed } from "./styles";

type ModifiedContractCheckoutQuery = Omit<
  ContractCheckoutQuery,
  "contractCheckout"
> & {
  contractCheckout: Omit<ContractCheckoutQuery["contractCheckout"], "lines"> & {
    lines: Array<
      Omit<
        ContractCheckoutQuery["contractCheckout"]["lines"][number],
        "contractProduct" | "product"
      > & {
        contractProduct: ContractCheckoutProductVariantQuery["contractCheckoutProductVariant"]["contractProduct"][number];
        product: ContractCheckoutQuery["contractCheckout"]["lines"][number]["product"] & {
          variants: ContractCheckoutProductVariantQuery["contractCheckoutProductVariant"]["productVariant"][number]["variants"];
        };
      }
    >;
  };
};

type ModifiedContractCheckoutLine = ModifiedContractCheckoutQuery["contractCheckout"]["lines"][number] & {
  errorQuantity?: boolean;
  contractId?: string;
  materialVariantCode?: string;
};

interface ShoppingCartDetailProps {
  checkOutId: string;
  onBack: () => void;
  onSubmitCart: (data, customerId, contractId) => void;
  params: OrderDetailQueryParams;
}

const EmptyShoppingCartDetail = () => {
  const classes = useStyles({});
  return (
    <Paper className={classes.emptyCart}>
      <SVG width={18} height={18} src={InfoIcon} className={classes.icon} />
      <MyTypography variant="body3">
        <FormattedMessage
          defaultMessage="ไม่มีสินค้าในตะกร้านี้"
          description="empty cart detail"
        />
      </MyTypography>
    </Paper>
  );
};

const ShoppingCartDetail: React.FC<ShoppingCartDetailProps> = props => {
  const { checkOutId, params, onSubmitCart } = props;
  const [listLineState, setListLineState] = useState<
    ModifiedContractCheckoutLine[]
  >([]);
  const [openConfrmDelete, setOpenConfirmDelete] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigator();
  const [data, setData] = useState<ModifiedContractCheckoutQuery | null>(null);
  const { data: baseData, loading: loadingData } = useContractCheckoutQuery({
    fetchPolicy: "no-cache",
    variables: {
      id: checkOutId,
      sortBy: getSortQueryOrderDetailVariables(params)
    },
    skip: !checkOutId
  });

  const { data: masterData } = useContractCheckoutProductVariantQuery({
    variables: { id: checkOutId }
  });

  const {
    data: cartInfoData,
    refetch: refetchCart
  } = useCartTotalCheckoutQuery({
    skip: true,
    fetchPolicy: "network-only"
  });

  const { data: contracts } = useContractsQuery({
    variables: {
      first: 50,
      soldToCode: data?.contractCheckout?.customer?.soldToCode,
      filter: {
        companyIds: []
      }
    },
    skip: !data?.contractCheckout?.customer?.soldToCode
  });

  useEffect(() => {
    try {
      if (
        !isEmpty(masterData) &&
        !isEmpty(baseData) &&
        baseData?.contractCheckout &&
        baseData.contractCheckout.lines &&
        masterData.contractCheckoutProductVariant
      ) {
        const {
          contractCheckout: { lines }
        } = baseData;
        const {
          contractCheckoutProductVariant: { contractProduct, productVariant }
        } = masterData;
        const newLines = lines.map(line => ({
          ...line,
          contractProduct: contractProduct?.find(
            contact => contact?.id === line.contractProduct?.id
          ),
          product: {
            ...line.product,
            variants: productVariant?.find(
              product => product?.id === line.product?.id
            )?.variants
          }
        }));
        setData({
          __typename: "Query",
          contractCheckout: {
            ...baseData.contractCheckout,
            lines: newLines
          }
        });
      }
    } catch (error) {
      console.error("Error processing contract checkout data:", error);
      setData(null);
    }
  }, [masterData, baseData]);

  const allContract = React.useMemo(
    () => contracts?.contracts?.edges?.map(({ node }) => node),
    [contracts?.contracts?.edges]
  );

  const getProjectName = React.useCallback(
    (contractNo: string) =>
      allContract?.find(contract => contract?.contractNo === contractNo)
        ?.projectName || "",
    [allContract]
  );

  const [deleteContractLines] = useDeleteContractCheckoutLinesMutation({
    onCompleted: data => {
      if (!data.deleteContractCheckoutLines.errors.length) {
        setListLineState(
          listLineState
            .filter(item => !item.selected)
            .map(item => ({ ...item, selected: false }))
            .sort((a, b) => +a.id - +b.id)
        );
        refetchCart();
      }
    }
  });
  const [, dispatchAppState] = useAppState();

  const intl = useIntl();

  const [updateCustomerCartLines] = useUpdateCustomerCartLinesMutation({
    onCompleted(data) {
      if (data) {
        refetchCart();
      }
    }
  });

  const findMaterialVariantCode = item => {
    const selectedVariantId = String(item?.variant?.id);
    return (
      (item?.product?.variants || [])?.find(
        variant => variant?.id === selectedVariantId
      )?.code || ""
    );
  };

  useEffect(() => {
    if (data && data.contractCheckout) {
      const listLine =
        data.contractCheckout?.lines?.map(item => ({
          ...item,
          contractId: item.contractProduct?.contract?.id,
          selected: true,
          variant: { ...item.variant, writable: true },
          materialVariantCode: findMaterialVariantCode(item),
          errorQuantity: false
        })) || [];
      handleChangeListLineState(listLine);
    } else {
      if (!loadingData) {
        navigate(orderDetailUrl(""));
      }
    }
  }, [data]);

  useEffect(
    () =>
      navigate(
        orderDetailUrl(checkOutId, {
          ...params
        }),
        { replace: true }
      ),
    []
  );

  useEffect(() => {
    if (cartInfoData) {
      const orderCount =
        cartInfoData.totalContractCheckouts?.totalProducts || 0;
      dispatchAppState({
        payload: {
          orderCount
        },
        type: "setOrderCount"
      });
    }
  }, [cartInfoData]);

  const classes = useStyles({});

  const getChoices = (item: ModifiedContractCheckoutLine) => {
    const itemProductVariants = item?.product?.variants || [];
    const listChoices = itemProductVariants.map(item => ({
      label: item.name,
      value: item.id,
      salesUnit: item.salesUnit,
      variantType: item.variantType,
      limitQuantity: item.limitQuantity,
      disabled: false
    }));
    const lineSameProduct = listLineState
      .filter(line => line.contractProduct?.id === item.contractProduct?.id)
      .filter(line => line.id !== item.id);
    const alreadySelect = lineSameProduct.map(line => line.variant.id);
    listChoices.forEach(choice => {
      if (alreadySelect.indexOf(choice.value) >= 0) {
        choice.disabled = true;
      }
    });

    const standardHeader = [
      {
        value: "",
        label: "สินค้า Standard",
        isSubHeader: true
      }
    ];

    const nonStandardHeader = [
      {
        value: "",
        label: "สินค้า Non Standard",
        isSubHeader: true
      }
    ];

    const standardList = listChoices.filter(
      e => e.variantType === VariantStandard.Standard
    );
    const nonStandardList = listChoices.filter(
      e => e.variantType === VariantStandard.NonStandard
    );

    if (standardList.length === 0 && nonStandardList.length === 0) {
      return listChoices;
    }

    return [].concat(
      standardHeader,
      standardList,
      nonStandardHeader,
      nonStandardList
    );
  };

  const onChangeChoices = (lineId, event, choice) => {
    const { salesUnit, limitQuantity } = choice;
    const choiceId = event.target.value;
    const lines = [...listLineState];
    const lineChange = lines.find(item => item.id === lineId);
    lineChange.variant.id = choiceId;
    lineChange.errorQuantity = lineChange.selected
      ? lineChange.quantity > limitQuantity
      : false;
    setListLineState(
      lines.map(item => {
        if (item.id === lineId) {
          return {
            ...item,
            materialVariantCode: findMaterialVariantCode(item),
            variant: {
              ...item.variant,
              salesUnit,
              limitQuantity,
              id: choiceId
            }
          };
        }
        return item;
      })
    );

    const newLines = lines.map(line => ({
      id: line.id,
      quantity: line.quantity,
      materialVariantId: line.variant.id
    }));

    updateCustomerCartLines({
      variables: {
        id: checkOutId,
        input: {
          lines: newLines
        }
      }
    });
  };

  const onChangeQuantity = (lineId, event) => {
    const lines = [...listLineState];
    const lineChange = lines.find(line => line.id === lineId);
    const lineGroupByProductId = lines.filter(
      line => line.product?.id === lineChange.product?.id && line.selected
    );

    const quantity = event.target.value;

    if (quantity) {
      lineChange.selected = true;
    }

    lineChange.quantity = Number(quantity);

    if (parseFloat(quantity)) {
      const remain = lineChange?.contractProduct?.remain || 0;
      const total = lineGroupByProductId.reduce(
        (count, line) =>
          count +
          Number(line.quantity || 0) * Number(line?.variant?.weight || 0),
        0
      );
      lineChange.errorQuantity = total > remain;
    } else {
      if (lineChange.selected) {
        lineChange.errorQuantity = true;
      }
    }
    handleChangeListLineState(lines, lineId);

    const newLines = lines.map(line => ({
      id: line.id,
      quantity: line.quantity,
      materialVariantId: line.variant.id
    }));

    if (parseInt(quantity, 10)) {
      updateCustomerCartLines({
        variables: {
          id: checkOutId,
          input: {
            lines: newLines
          }
        }
      });
    }
  };

  const onChangeCheckItem = (lineId: string, event) => {
    const lines = [...listLineState];
    const lineChange = lines.find(item => item.id === lineId);

    const checked = event.target.checked;
    lineChange.selected = checked;

    if (!checked) {
      lineChange.errorQuantity = false;
    }
    if (!parseFloat(lineChange.quantity?.toString())) {
      lineChange.errorQuantity = checked;
    }
    handleChangeListLineState(lines, lineId);
  };

  const getSelectedLine = () => listLineState.filter(item => item.selected);

  const getCheckedHeader = () => listLineState.every(item => item.selected);

  const isSomeLineErrorQuantity = () =>
    listLineState.some(item => item.errorQuantity);

  const onClickCheckHeader = () => {
    const lines = [...listLineState];
    const listSelected = getSelectedLine();
    if (listSelected.length < listLineState.length) {
      lines.forEach(line => {
        line.selected = true;
      });
    } else if (listSelected.length === listLineState.length) {
      lines.forEach(item => {
        item.selected = false;
      });
    }
    handleChangeListLineState(lines);
  };

  const handleChangeListLineState = (
    newList: ModifiedContractCheckoutLine[],
    lineId = null
  ) => {
    const calculator = {};
    const productByLineId = newList?.find(item => item?.id === lineId);
    const lineSameProduct = newList
      .filter(
        item =>
          item?.contractProduct?.id === productByLineId?.contractProduct?.id
      )
      .map(item => item?.id);

    newList.forEach(line => {
      if (!line?.selected) {
        return;
      }
      const product = calculator[line?.contractProduct?.id];
      if (product) {
        calculator[line?.contractProduct?.id] = {
          total:
            product.total +
            Number(line?.quantity || 0) * Number(line?.variant.weight || 0),
          remain: line?.contractProduct?.remain
        };
      } else {
        calculator[line?.contractProduct?.id] = {
          total:
            Number(line?.quantity || 0) * Number(line?.variant.weight || 0),
          remain: line?.contractProduct?.remain
        };
      }
    });

    const lines = [...newList];
    lines.forEach(line => {
      if (lineId && !lineSameProduct.includes(line?.id)) {
        return;
      }

      if (line?.selected) {
        if (!line?.quantity || parseFloat(line?.quantity?.toString()) === 0) {
          line.errorQuantity = true;
          return;
        }
      } else {
        line.errorQuantity = false;
        return;
      }
      if (lineId && line?.id !== lineId && !line?.errorQuantity) {
        return;
      }

      const calculatorByProduct = calculator[line?.contractProduct?.id];

      const basePrdGrp =
        line?.contractProduct?.contract?.prcGroup1 === "K02" ||
        line?.contractProduct?.contract?.prcGroup1 === "K04" ||
        line?.contractProduct?.contract?.prcGroup1 === "K06";

      if (basePrdGrp && line?.quantity > calculatorByProduct?.remain) {
        line.errorQuantity = true;
      } else if (
        calculatorByProduct &&
        calculatorByProduct?.total > calculatorByProduct?.remain
      ) {
        line.errorQuantity = true;
      } else {
        line.errorQuantity = false;
      }
    });
    setListLineState(lines);
  };

  const getIndeterminateHeader = () => {
    const listSelectedLength = getSelectedLine().length;
    return listSelectedLength > 0 && listSelectedLength < listLineState.length;
  };

  const confirmDelete = async () => {
    const listId = getSelectedLine().map(item => item.id);
    setLoading(true);
    setOpenConfirmDelete(false);
    try {
      await deleteContractLines({
        variables: {
          checkoutLineIds: listId
        }
      });
      setLoading(false);
    } catch {
      setLoading(false);
    }
  };

  const getDisableSubmit = () => {
    const listSelected = getSelectedLine();

    if (listSelected?.length <= 0) {
      return true;
    }

    return false;
  };

  const onClickSubmitBottom = () => {
    const lines = getSelectedLine();
    const dataSubmit = {
      detailId: data.contractCheckout.id,
      contractId: data.contractCheckout.contract.id,
      userId: data.contractCheckout.customer.id,
      lines: lines.map(item => ({
        id: item.id,
        quantity: item.quantity,
        variantId: item.variant.id,
        selected: item.selected
      }))
    };
    const customerId = data?.contractCheckout?.customer?.id;
    const contractId =
      lines[0]?.contractId || data?.contractCheckout?.contract?.id;
    onSubmitCart(dataSubmit, customerId, contractId);
  };

  const handleSort = createSortHandler(
    navigate,
    orderDetailUrlParam(checkOutId),
    params
  );

  const sort = getSortParams(params);

  const textError = intl.formatMessage({
    defaultMessage: "จำนวนที่สั่งมีมากกว่าจำนวนคงเหลือ"
  });

  const isMatType84 = product => product.materialType === "84";

  if (!data) {
    return null;
  }

  return (
    <>
      <BoxInfoCustomer data={data} count={listLineState.length} />
      <div className={classes.tableProduct}>
        <div className={classes.tableTitle}>
          <MyTypography variant={"body2"}>
            <FormattedMessage defaultMessage={"รายการสินค้า"} />
          </MyTypography>
        </div>
        <div className={classes.root} ref={containerRef}>
          {listLineState.length ? (
            <Table className={classes.mainTable}>
              <TableHead>
                <TableRow>
                  <TableCellHeader style={{ minWidth: 35 }}>
                    <Checkbox
                      id="checkboxAll"
                      data-testid="checkboxAll"
                      size={"small"}
                      onClick={onClickCheckHeader}
                      checked={getCheckedHeader()}
                      indeterminate={getIndeterminateHeader()}
                    />
                  </TableCellHeader>
                  <TableCellHeader
                    style={{ minWidth: 220 }}
                    arrowPosition={"right"}
                    direction={
                      sort.sort === OrderDetailSortField.contract
                        ? getArrowDirection(sort.asc)
                        : undefined
                    }
                    onClick={() => handleSort(OrderDetailSortField.contract)}
                  >
                    <FormattedMessage defaultMessage={"เลขที่ Contract"} />
                  </TableCellHeader>
                  <TableCellHeader style={{ minWidth: 200 }} textAlign={"left"}>
                    <FormattedMessage defaultMessage={"Material Code"} />
                  </TableCellHeader>
                  <TableCellHeader style={{ minWidth: 200 }} textAlign={"left"}>
                    <FormattedMessage defaultMessage={"ช่ือสินค้า"} />
                  </TableCellHeader>
                  <TableCellHeader style={{ minWidth: 200 }} textAlign={"left"}>
                    <FormattedMessage defaultMessage={"Material Code"} />
                  </TableCellHeader>
                  <TableCellHeader style={{ minWidth: 300 }}>
                    <FormattedMessage defaultMessage={"รายละเอียดสินค้า"} />
                  </TableCellHeader>
                  <TableCellHeader style={{ minWidth: 80 }}>
                    <FormattedMessage defaultMessage={"จำนวนสั่งซื้อ"} />
                  </TableCellHeader>
                  <TableCellHeader
                    style={{ minWidth: 100 }}
                    textAlign={"center"}
                  >
                    <FormattedMessage defaultMessage={"หน่วยซื้อ"} />
                  </TableCellHeader>
                  <TableCellHeader
                    style={{ minWidth: 130 }}
                    className={classNames(classes.cellBackground)}
                    textAlign={"right"}
                    arrowPosition={"right"}
                    direction={
                      sort.sort === OrderDetailSortField.total
                        ? getArrowDirection(sort.asc)
                        : undefined
                    }
                    onClick={() => handleSort(OrderDetailSortField.total)}
                  >
                    {intl.formatMessage({ defaultMessage: "จำนวน" })}
                  </TableCellHeader>
                  <TableCellHeader
                    style={{ minWidth: 130 }}
                    className={classNames(classes.cellBackground)}
                    textAlign={"center"}
                  >
                    {intl.formatMessage({ defaultMessage: "คงเหลือ" })}
                  </TableCellHeader>
                  <TableCellHeader
                    style={{ minWidth: 120 }}
                    className={classNames(classes.cellBackground)}
                    textAlign={"center"}
                  >
                    {intl.formatMessage({ defaultMessage: "หน่วยขาย" })}
                  </TableCellHeader>
                  <TableCellHeader
                    style={{ minWidth: 180 }}
                    className={classNames(classes.cellBackground)}
                    textAlign={"right"}
                    arrowPosition={"right"}
                    direction={
                      sort.sort === OrderDetailSortField.price
                        ? getArrowDirection(sort.asc)
                        : undefined
                    }
                    onClick={() => handleSort(OrderDetailSortField.price)}
                  >
                    {intl.formatMessage({
                      defaultMessage: "ราคา"
                    })}
                    <span className={classes.subtitle}>
                      ({data?.contractCheckout?.contract?.currency})
                    </span>
                    {intl.formatMessage({
                      defaultMessage: "/หน่วยขาย"
                    })}
                  </TableCellHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {listLineState.length ? (
                  listLineState
                    .sort((a, b) => +a.id - +b.id)
                    .map((item, index) => (
                      <TableRow className={classes.tableRow} key={item.id}>
                        <TableCell>
                          <Checkbox
                            id={`lines[${index}].checkbox`}
                            data-testid={`lines[${index}].checkbox`}
                            size={"small"}
                            checked={item.selected}
                            onChange={e => onChangeCheckItem(item.id, e)}
                          />
                        </TableCell>
                        <TableCell className={classes.colNumber} align={"left"}>
                          {`${
                            item?.contractProduct?.contract?.contractNo
                          } - ${getProjectName(
                            item?.contractProduct?.contract?.contractNo
                          )}`}
                        </TableCell>
                        <TableCell className={classes.colProductName}>
                          {item?.product?.materialCode}
                        </TableCell>
                        <TableCell className={classes.colProductName}>
                          {item?.contractProduct?.materialDescription}
                        </TableCell>
                        <TableCell className={classes.colProductName}>
                          {isMatType84(item.product)
                            ? item?.materialVariantCode
                            : null}
                        </TableCell>
                        <TableCell>
                          {isMatType84(item.product) ? (
                            <SingleSelectField
                              id={`lines[${index}].selectItem`}
                              data-testid={`lines[${index}].selectItem`}
                              choices={getChoices(item)}
                              onChange={(e, choice) => {
                                onChangeChoices(item.id, e, choice);
                              }}
                              value={item.variant.id}
                            />
                          ) : (
                            ""
                          )}
                        </TableCell>
                        <TableCell>
                          <NumberFormat
                            id={`lines[${index}].quantity`}
                            error={item.errorQuantity}
                            placeholder={
                              SALE_CATEG_1(
                                item.variant?.salesUnit ??
                                  item?.product?.purchaseUnit
                              )
                                ? "0.000"
                                : "0"
                            }
                            onChange={e => onChangeQuantity(item.id, e)}
                            value={item.quantity?.toString()}
                            decimalScale={
                              SALE_CATEG_1(
                                item.variant?.salesUnit ??
                                  item?.product?.purchaseUnit
                              )
                                ? 3
                                : 0
                            }
                            allowLeadingZeros={
                              !SALE_CATEG_1(
                                item.variant?.salesUnit ??
                                  item?.product?.purchaseUnit
                              )
                            }
                            rightAlignPlaceholder={SALE_CATEG_1(
                              item.variant?.salesUnit ??
                                item?.product?.purchaseUnit
                            )}
                            fixedDecimalScale={SALE_CATEG_1(
                              item.variant?.salesUnit ??
                                item?.product?.purchaseUnit
                            )}
                          />
                          {item.errorQuantity && item.quantity ? (
                            <div className={classes.textError}>{textError}</div>
                          ) : (
                            ""
                          )}
                        </TableCell>
                        <TableCell
                          align={"center"}
                          className={classes.customerName}
                        >
                          {item.variant?.salesUnit ??
                            item?.product?.purchaseUnit}
                        </TableCell>
                        <TableCell
                          className={classNames(classes.cellBackground)}
                          align={"right"}
                        >
                          <DecimalFormat
                            value={item.contractProduct?.total}
                            isGrayDecimal
                            countDecimal={3}
                          />
                        </TableCell>
                        <TableCell
                          align={"right"}
                          className={classNames(classes.cellBackground)}
                        >
                          <DecimalFormat
                            value={item.contractProduct?.remain}
                            isGrayDecimal
                            countDecimal={3}
                          />
                        </TableCell>
                        <TableCell
                          align={"center"}
                          className={classNames(classes.cellBackground)}
                        >
                          {item?.contractProduct?.salesUnit}
                        </TableCell>
                        <TableCell
                          align={"right"}
                          className={classNames(classes.cellBackground)}
                        >
                          <DecimalFormat
                            value={item.contractProduct?.price}
                            countDecimal={2}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                ) : (
                  <Skeleton />
                )}
              </TableBody>
            </Table>
          ) : (
            <EmptyShoppingCartDetail />
          )}
          {listLineState?.length ? (
            <StickyHorizontalScrollBar
              container={containerRef}
              bottom={bottomFixed + 10}
            />
          ) : null}
        </div>
      </div>
      <BottomBar
        selectedLine={getSelectedLine().length}
        disabled={getDisableSubmit() || isSomeLineErrorQuantity() || loading}
        disabledDelete={getDisableSubmit() || loading}
        onClick={() => setOpenConfirmDelete(true)}
        onSubmit={onClickSubmitBottom}
      />
      <DialogConfirmDelete
        onConfirm={confirmDelete}
        onCancel={() => setOpenConfirmDelete(false)}
        open={openConfrmDelete}
        title={"ลบรายการสินค้าที่เลือก!"}
        content={"คุณกำลังจะลบรายการสินค้าที่เลือกในตะกร้านี้"}
      />
    </>
  );
};

export default ShoppingCartDetail;
